"use client";

import { useMemo } from "react";
import type { DiatonicChord } from "@/lib/music/chord-catalog";
import { usePressedPitchClasses } from "./use-pressed-pitch-classes";

/**
 * Indices (0..n-1) des accords diatoniques dont **toutes** les
 * pitch-classes sont actuellement pressées au piano.
 *
 * Octave-agnostique : un accord joué en n'importe quelle inversion ou
 * voicing matche dès qu'on couvre l'intégralité de ses pitch-classes
 * (root, tierce, quinte pour une triade, `entry.pitchClasses` est
 * déjà calculé par `chord-catalog.ts:buildEntry`). Tolère les notes en
 * plus : un IV "enrichi" reste reconnu.
 *
 * Renvoie un set vide tant qu'aucune note n'est pressée.
 */
export function useActiveDiatonicDegrees(
  diatonic: ReadonlyArray<DiatonicChord>,
): ReadonlySet<number> {
  const pressedPcs = usePressedPitchClasses();

  return useMemo(() => {
    const set = new Set<number>();

    if (pressedPcs.size === 0) {
      return set;
    }

    diatonic.forEach((dc, idx) => {
      const pcs = dc.entry.pitchClasses;

      if (pcs.length === 0) {
        return;
      }

      if (pcs.every((pc) => pressedPcs.has(pc))) {
        set.add(idx);
      }
    });

    return set;
  }, [diatonic, pressedPcs]);
}
